import { Outlet } from "react-router-dom";
import { type ComponentType } from "react";
import type { RouteRecord } from "vite-react-ssg";
import ScrollToTop from "./components/ScrollToTop";
import AutoLayout from "./components/layout/AutoLayout";
import sitemapRoutes from "./sitemap-routes";

// Root layout — bewust minimaal om kritische JS klein te houden.
// - Geen QueryClientProvider: @tanstack/react-query wordt nergens gebruikt.
// - Geen Toaster / Sonner: er wordt nergens `toast(...)` aangeroepen.
// - Geen root TooltipProvider: alleen PriceConfigurator gebruikt tooltips en wrapt zichzelf al.
// - HelmetProvider komt uit vite-react-ssg zelf — hier NIET nogmaals mounten.
const Root = () => (
  <>
    <ScrollToTop />
    <Outlet />
  </>
);

// Vite scant statisch alle `src/pages/*.tsx` bestanden voor lazy loading.
const pageModules = import.meta.glob("./pages/*.tsx") as Record<
  string,
  () => Promise<{ default: ComponentType<unknown> }>
>;

// Auto-genereer child routes uit sitemap-routes.ts (pagina's met componentPath + seoTitle).
const autoChildren: RouteRecord[] = sitemapRoutes
  .filter((r) => r.componentPath && r.seoTitle)
  .map((r) => {
    const key = `./pages/${r.componentPath}.tsx`;
    const loader = pageModules[key];
    if (!loader) {
      console.warn(`⚠️ Pagina-component niet gevonden: ${key}`);
      return null;
    }
    const childPath = r.path === "/" ? undefined : r.path.replace(/^\//, "");
    return {
      ...(childPath ? { path: childPath } : { index: true }),
      entry: key.replace(/^\.\//, "src/"),
      lazy: async () => {
        const mod = await loader();
        const Page = mod.default;
        const Wrapped = () => (
          <AutoLayout route={r}>
            <Page />
          </AutoLayout>
        );
        return { Component: Wrapped };
      },
    } as RouteRecord;
  })
  .filter((r): r is RouteRecord => r !== null);

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: Root,
    children: [
      ...autoChildren,
      {
        path: "diensten/:slug",
        lazy: async () => {
          const mod = await import("./pages/ServicePage");
          return { Component: mod.default };
        },
      },
      {
        path: "*",
        lazy: async () => {
          const mod = await import("./pages/NotFound");
          return { Component: mod.default };
        },
      },
    ],
  },
];

export default routes;
